import React, { useState, useEffect } from 'react';
import { Select, Input, Button, message, Tooltip, Spin } from 'antd';
import { DocumentIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs'; // Ensure dayjs is installed and imported

const { Option } = Select;
const { TextArea } = Input;

// Helper function to get the current quarter (e.g., "Q1", "Q2")
const getCurrentQuarter = () => {
  const month = dayjs().month(); // 0-11
  return `Q${Math.floor(month / 3) + 1}`;
};

// Helper function to get the current year as a string
const getCurrentYear = () => {
  return dayjs().year().toString(); // Return as string
};

const CompactTouchpointForm = ({ onAddRow, onDataSubmitted }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    // Updated state fields
    reporting_quarter: getCurrentQuarter(),
    reporting_year: getCurrentYear(), // Already a string from getCurrentYear()
    stakeholder: '',
    touchpoint: '',
    rewrite_with_ai: false, // Kept as per original logic
    include_in_annual_review_report: 'No',
    include_in_next_years_master_spreadsheet: 'No',
    time_spent: 30,
    date: dayjs().format('YYYY-MM-DD') // Use dayjs for consistency
  });

  const [companyOptions, setCompanyOptions] = useState([]);
  const [isRewriting, setIsRewriting] = useState(false);

  // Fetch client names when component mounts (no changes needed here)
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
        // Fetching based on the operational year might still be relevant
        const operationalYear = dayjs().year().toString(); // Convert to string
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${operationalYear}`, {
          headers: {
            'user-id': userId
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        let options = data.client_names.map(name => ({
          value: name,
          label: name
        }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        options.unshift({ value: 'All Clients', label: 'All Clients' });
        setCompanyOptions(options);
      } catch (error) {
        console.error('Error fetching client names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
  }, []);

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  // --- Form Validation Update ---
  const isFormValid = () => {
    const valid = {
      company_name: Boolean(formData.company_name),
      // Check new fields
      reporting_quarter: Boolean(formData.reporting_quarter), // Allow 'All' as a valid reporting quarter
      reporting_year: Boolean(formData.reporting_year), // Year should always be present
      stakeholder: Boolean(formData.stakeholder),
      touchpoint: Boolean(formData.touchpoint.trim())
    };

    // Keep detailed logging for debugging validation issues
    console.log('Form validation details:', {
      ...valid,
      touchpoint_length: formData.touchpoint.length,
      touchpoint_trimmed_length: formData.touchpoint.trim().length
    });

    // Return true if all required fields are valid
    return valid.company_name &&
           valid.reporting_quarter &&
           valid.reporting_year && // Ensure year is validated
           valid.stakeholder &&
           valid.touchpoint;
  };

  const handleSubmit = async () => {
    try {
      if (!isFormValid()) {
        // Log current state when validation fails
        console.log('Form validation failed. Current form data:', formData);
        message.error('Please fill in all required fields (Company, Reporting Quarter, Stakeholder, Touchpoint)');
        return;
      }

      const userId = localStorage.getItem('userid');
      // Use reporting_year from state for the API endpoint year if appropriate,
      // or keep using current year if endpoint is for operational context.
      // Let's assume the endpoint year refers to the *operational* year of submission.
      const operationalYear = dayjs().year().toString(); // Convert to string

      // --- Transform Data Update ---
      const transformedData = {
        company_names: formData.company_name === 'All Clients' ? [] : [formData.company_name],
        // Send new fields to the API
        reporting_quarter: formData.reporting_quarter,
        reporting_year: formData.reporting_year, // Now sending as string value
        stakeholders: [formData.stakeholder],
        touchpoint: formData.touchpoint,
        rewrite_with_ai: false, // Still false as per original requirement
        include_in_annual_review_report: formData.include_in_annual_review_report,
        include_in_next_years_master_spreadsheet: formData.include_in_next_years_master_spreadsheet,
        time_spent: parseInt(formData.time_spent), // Ensure time_spent is an integer
        date: formData.date
      };

      // Use the determined operational year for the API endpoint
      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/touchpoints/${operationalYear}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
          'accept': 'application/json'
        },
        body: JSON.stringify(transformedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Attempt to provide a more specific error message from the API response
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json(); // Assuming API returns some confirmation data
      message.success('Touchpoint added successfully');

      // --- Form Reset Update ---
      setFormData({
        company_name: '',
        // Reset new fields to current defaults
        reporting_quarter: getCurrentQuarter(),
        reporting_year: getCurrentYear(),
        stakeholder: '',
        touchpoint: '',
        rewrite_with_ai: false,
        include_in_annual_review_report: 'No',
        include_in_next_years_master_spreadsheet: 'No',
        time_spent: 30,
        date: dayjs().format('YYYY-MM-DD')
      });

      // --- Callback Update ---
      if (onAddRow) {
        onAddRow({
          CompanyName: formData.company_name,
          // Pass new fields to callback
          ReportingQuarter: formData.reporting_quarter,
          ReportingYear: formData.reporting_year,
          Stakeholder: formData.stakeholder,
          Touchpoint: formData.touchpoint, // Pass the original touchpoint text
          TimeSpent: formData.time_spent,
          Date: formData.date
          // Note: The *potentially* rewritten text isn't passed here, only the final submitted text.
        });
      }

      if (onDataSubmitted) {
        onDataSubmitted(); // Notify parent component of submission
      }
    } catch (error) {
      console.error('Error submitting touchpoint:', error);
      message.error(error.message || 'Failed to submit touchpoint');
    }
  };

  // Rewrite text function (no changes needed unless API changes)
  const handleRewriteText = async () => {
    if (!formData.touchpoint.trim()) {
      message.warning('Please enter some text to rewrite');
      return;
    }

    setIsRewriting(true);
    try {
      const response = await fetch('https://plansync-backend-234288438538.us-central1.run.app/api/rewrite_touchpoint_text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: formData.touchpoint })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rewrittenData = await response.json();
      // Assuming the API returns JSON like { rewritten_text: "..." }
      // Adjust if the API response structure is different
      const rewrittenText = rewrittenData.rewritten_text || rewrittenData.toString();

      await new Promise(resolve => setTimeout(resolve, 500)); // UI delay

      setFormData(prevData => ({
        ...prevData,
        touchpoint: rewrittenText // Update touchpoint state with rewritten text
      }));

      message.success('Text rewritten successfully');
    } catch (error) {
      console.error('Error rewriting text:', error);
      message.error('Failed to rewrite text');
    } finally {
      setIsRewriting(false);
    }
  };

  // Options for dropdowns
  // --- Quarter Options Update ---
  // 'All' is now included as an option for the reporting quarter dropdown
  const reportingQuarterOptions = ['All', 'Q1', 'Q2', 'Q3', 'Q4'].map(q => ({ value: q, label: q }));

  // No changes needed for stakeholder options unless requirements changed
  const stakeholderOptions = [
    'Plan Sponsor',
    'Participants',
    'TPA',
    'Recordkeeper'
  ].map(s => ({ value: s, label: s }));

  // Helper to render labels with tooltips (no changes needed)
  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2 cursor-help" />
      </Tooltip>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-md"> {/* Added padding and shadow */}
      <h1 className="text-2xl font-light text-gray-800 mb-6">Touchpoint Data Entry</h1> {/* Slightly smaller heading */}
      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}> {/* Prevent default form submission */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4"> {/* Adjusted grid for potentially adding year */}
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Company Name", "Select a company for this touchpoint.")}
            </label>
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
              value={formData.company_name || undefined} // Handle empty initial value
              onChange={(value) => handleChange('company_name', value)}
              placeholder="Select a company"
            >
              {companyOptions.map(option => (
                <Option key={option.value} value={option.value} label={option.label}>{option.label}</Option>
              ))}
            </Select>
          </div>

          {/* --- Reporting Quarter --- */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Reporting Quarter", "Select the quarter this touchpoint relates to for reporting.")}
            </label>
            <Select
              style={{ width: '100%' }}
              value={formData.reporting_quarter}
              onChange={(value) => handleChange('reporting_quarter', value)}
            >
              {reportingQuarterOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>

          {/* --- Reporting Year (Display Only/Implicit) --- */}
          {/* You could add a selector here if needed, but for now, it uses the current year */}
          <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Reporting Year", "The year this touchpoint relates to for reporting.")}
            </label>
             <Select
               style={{ width: '100%' }}
               value={formData.reporting_year}
               onChange={(value) => handleChange('reporting_year', value)}
             >
                {/* Generate year options dynamically */}
                {['All', (getCurrentYear() - 2).toString(), (getCurrentYear() - 1).toString(), getCurrentYear()].map(year => (
                  <Option key={year} value={year}>{year}</Option>
                ))}
             </Select>
          </div>
        </div>

        {/* Stakeholder */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Stakeholder", "Select the primary party involved. Search by typing.")}
          </label>
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            value={formData.stakeholder || undefined} // Handle empty initial value
            onChange={(value) => handleChange('stakeholder', value)}
            placeholder="Select a stakeholder"
          >
            {stakeholderOptions.map(option => (
              <Option key={option.value} value={option.value} label={option.label}>{option.label}</Option>
            ))}
          </Select>
        </div>

        {/* Touchpoint Text Area & AI Rewrite */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Touchpoint", "Describe the interaction, meeting, or communication.")}
          </label>
          <div className="space-y-2">
            <div className="relative">
              <Spin spinning={isRewriting} tip="AI is rewriting your text...">
                <div className={`transition-opacity duration-300 ${isRewriting ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}> {/* Disable interaction when spinning */}
                  <TextArea
                    value={formData.touchpoint}
                    onChange={(e) => handleChange('touchpoint', e.target.value)}
                    rows={4} // Slightly more rows maybe
                    placeholder="Enter touchpoint details..."
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" // Added more styling
                    disabled={isRewriting} // Still good practice
                  />
                </div>
              </Spin>
            </div>
            <Button
              onClick={handleRewriteText}
              icon={<SparklesIcon className={`h-5 w-5 mr-1 ${isRewriting ? 'animate-spin' : ''}`} />} // Adjusted margin
              className={`
                inline-flex items-center px-3 py-1.5 border border-transparent
                text-xs font-medium rounded shadow-sm text-white
                ${isRewriting ? 'bg-indigo-400 cursor-wait' : formData.touchpoint.trim() ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'}
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                transition-all duration-200
              `} // Smaller button
              disabled={isRewriting || !formData.touchpoint.trim()}
              size="small" // Antd size prop
            >
              {isRewriting ? 'Rewriting...' : 'Rewrite with AI'}
            </Button>
          </div>
        </div>

        {/* Include Flags */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Include in Annual Review", "Include in the annual review report?")}
            </label>
            <Select
              style={{ width: '100%' }}
              value={formData.include_in_annual_review_report}
              onChange={(value) => handleChange('include_in_annual_review_report', value)}
            >
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Include in Next Year's Data", "Include in next year's planning data?")}
            </label>
            <Select
              style={{ width: '100%' }}
              value={formData.include_in_next_years_master_spreadsheet}
              onChange={(value) => handleChange('include_in_next_years_master_spreadsheet', value)}
            >
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
            </Select>
          </div>
        </div>

        {/* Time Spent & Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Time spent (minutes)", "Approximate duration of this touchpoint.")}
            </label>
            <Select
              style={{ width: '100%' }}
              value={formData.time_spent}
              onChange={(value) => handleChange('time_spent', parseInt(value))} // Ensure value is integer
            >
              {/* More time options might be useful */}
              {[15, 30, 45, 60, 75, 90, 120].map(time => (
                <Option key={time} value={time}>{time} min</Option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Date of Occurrence", "Date when the touchpoint occurred.")}
            </label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              style={{ width: '100%' }}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              max={dayjs().format('YYYY-MM-DD')} // Prevent selecting future dates
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-start pt-4"> {/* Added padding-top */}
          <Tooltip title={!isFormValid() ? "Please fill in all required fields first" : "Submit this touchpoint to the database"}>
            {/* Button wrapper needed for Tooltip on disabled elements */}
            <span className="inline-block">
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid()}
                size="large"
                type="primary" // Use Ant Design's primary button style
                icon={<DocumentIcon className="h-5 w-5 mr-2" />}
                className={`inline-flex items-center !text-lg !font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out ${!isFormValid() ? '!bg-gray-400 !border-gray-400 !cursor-not-allowed' : '!bg-indigo-600 hover:!bg-indigo-700 !border-indigo-600 hover:!border-indigo-700'}`}
                style={{ cursor: !isFormValid() ? 'not-allowed' : 'pointer' }} // Explicit cursor style
              >
                Submit Touchpoint
              </Button>
            </span>
          </Tooltip>
        </div>
      </form>
    </div>
  );
};

export default CompactTouchpointForm;